/** Route → required permission slug (first match wins). */
const ROUTE_RULES = [
  { prefix: "/waiter", permission: "waiter.view" },
  { prefix: "/pos", permission: "pos.view" },
  { prefix: "/tables", permission: "pos.view" },
  { prefix: "/delivery", permission: "delivery.view" },
  { prefix: "/masters", permission: "masters.view" },
  { prefix: "/accounts", permission: "accounts.view" },
  { prefix: "/reports", permission: "reports.view" },
  { prefix: "/admin", permission: "administration.view" },
] as const;

const DASHBOARD_PERMS = [
  "reports.view",
  "administration.view",
  "accounts.view",
  "masters.view",
] as const;

export function can(permissions: string[] | undefined, slug: string): boolean {
  if (!slug) return true;
  return (permissions ?? []).includes(slug);
}

export function canAccessDashboard(permissions: string[] | undefined): boolean {
  return DASHBOARD_PERMS.some((p) => can(permissions, p));
}

/**
 * Waiter ordering UI is for dedicated waiter staff only — not admin/manager/cashier
 * users who happen to have waiter.view via the owner role.
 */
/** KDS / kitchen order board — kitchen staff, POS supervisors, and back-office admins. */
export function canViewKitchenOrders(permissions: string[] = []): boolean {
  if (can(permissions, "kitchen-display.view")) return true;
  if (canAccessDashboard(permissions)) return true;
  if (can(permissions, "pos.view")) return true;
  return false;
}

export function canUseWaiterScreen(permissions: string[] = []): boolean {
  if (!can(permissions, "waiter.view")) return false;
  if (canAccessDashboard(permissions)) return false;
  if (can(permissions, "administration.view")) return false;
  if (permissions.some((p) => p.startsWith("pos."))) return false;
  return true;
}

export function canAccessPath(pathname: string, permissions: string[] | undefined): boolean {
  if (pathname === "/" || pathname === "") {
    return canAccessDashboard(permissions);
  }

  if (pathname === "/waiter" || pathname.startsWith("/waiter/")) {
    return canUseWaiterScreen(permissions ?? []);
  }

  if (pathname === "/kds" || pathname.startsWith("/kds/")) {
    return canViewKitchenOrders(permissions ?? []);
  }

  if (pathname === "/online" || pathname.startsWith("/online/")) {
    return can(permissions, "pos.view") || can(permissions, "masters.view");
  }

  for (const rule of ROUTE_RULES) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      return can(permissions, rule.permission);
    }
  }

  return true;
}

/** Counter / waiter staff without back-office modules. */
export function isMinimalStaff(permissions: string[] = []): boolean {
  const hasBackOffice = permissions.some(
    (p) =>
      p.startsWith("administration.")
      || p.startsWith("accounts.")
      || p.startsWith("reports.")
      || p.startsWith("masters."),
  );
  if (hasBackOffice) return false;
  return permissions.some(
    (p) => p.startsWith("pos.") || p.startsWith("waiter.") || p.startsWith("kitchen-display."),
  );
}

export function getDefaultRoute(permissions: string[] = []): string {
  if (canAccessDashboard(permissions)) return "/";

  const hasPos = permissions.some((p) => p.startsWith("pos."));
  const hasKds = permissions.some((p) => p.startsWith("kitchen-display."));

  if (hasPos) return "/pos";
  if (canUseWaiterScreen(permissions)) return "/waiter";
  if (hasKds) return "/kds";
  return "/login";
}

/** After login, only return to the prior page for front-line staff (not back-office). */
export function getPostLoginRoute(permissions: string[] = [], fromPath?: string): string {
  const home = getDefaultRoute(permissions);
  if (
    fromPath
    && fromPath !== "/login"
    && canAccessPath(fromPath, permissions)
    && !canAccessDashboard(permissions)
  ) {
    return fromPath;
  }
  return home;
}

export interface MenuItemWithPermission {
  permission?: string;
  accessCheck?: (permissions: string[]) => boolean;
  to?: string;
  [key: string]: unknown;
}

export function filterMenuItems<T extends MenuItemWithPermission>(items: T[], permissions: string[]): T[] {
  return items.filter((item) => {
    if (item.to === "/waiter" && !canUseWaiterScreen(permissions)) return false;
    if (item.accessCheck) return item.accessCheck(permissions);
    return !item.permission || can(permissions, item.permission);
  });
}

export function filterMenus<T extends { items: MenuItemWithPermission[] }>(menus: T[], permissions: string[]): T[] {
  return menus
    .map((menu) => ({
      ...menu,
      items: filterMenuItems(menu.items, permissions),
    }))
    .filter((menu) => menu.items.length > 0);
}
